import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
import { useWebSocket } from '@/hooks/useWebSocket.tsx';
import { AppError } from '@/types/errors';

export enum Signals {
  Offer = 'offer',
  Answer = 'answer',
  IceCandidate = 'ice-candidate',
  UserMediaSet = 'user-media-set',
  Start = 'start',
  BePolite = 'be-polite',
  Close = 'close',
  Closed = 'closed',
  SkipUser = 'skip-user',
  StartSession = 'start-session',
  StopSession = 'stop-session',
  ICEServers = 'ice-servers',
  PeerConnectionDetail = 'peer-connection-detail',
  UserData = 'user-data',
  UpdateGeoFilter = 'update-geo-filter',
  UpdateGenderFilter = 'update-gender-filter',
  // Session challenge for duplicate connection verification
  SessionChallenge = 'session-challenge',
  SessionChallengeResponse = 'session-challenge-response',
}

interface Connection {
  peerId: string;
}

interface GeoFilter {
  enabled: boolean;
  mode: 'whitelist' | 'blacklist';
  countries: string[];
}

interface GenderFilter {
  enabled: boolean;
  preference: 'male' | 'female';
}

export interface SignalingMessageType {
  signal: Signals;
  description?: RTCSessionDescription;
  candidate?: RTCIceCandidate;
  iceServers?: RTCIceServer[];
  connection?: Connection;
  user?: string;
  geoFilter?: GeoFilter;
  genderFilter?: GenderFilter;
  error?: AppError;
}

export type ReconnectionState = {
  attempt: number;
  nextRetryTime: number | null;
  giveUpTime: number | null;
};

// Define the context type
interface SignalingContextType {
  isConnected: boolean;
  addSignalingListener: (type: string, callback: (event: any) => void) => void;
  removeSignalingListener: (type: string, callback?: (event: any) => void) => void;
  sendSignal: (signal: SignalingMessageType) => void;
  close: () => void;
  reconnect: () => void;
  reconnectionState: ReconnectionState | null;
}

// Create the context with a default value
const SignalingContext = createContext<SignalingContextType | undefined>(undefined);

// Provider props
interface SignalingProviderProps {
  children: ReactNode;
  wsUrl: string;
  autoReconnect?: boolean;
  reconnectInterval?: number;
  maxReconnectTime?: number;
}

// Create the provider component
export const SignalingProvider: React.FC<SignalingProviderProps> = ({
  children,
  wsUrl,
  autoReconnect = true,
  reconnectInterval = 1000,
  maxReconnectTime = 30 * 60 * 1000 // 30 minutes
}) => {

  const options = useMemo(() => ({
    autoReconnect,
    reconnectInterval,
    maxReconnectTime
  }), []);

  const {
    isConnected,
    addEventListener,
    removeEventListener,
    sendMessage,
    close,
    reconnect,
    reconnectionState
  } = useWebSocket<SignalingMessageType>(wsUrl, options);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data) as SignalingMessageType;

        // Handle session challenge - automatically respond to prove connection is alive
        // This is used by the server to distinguish between:
        // - A dead connection (network drop) - no response, allow new connection
        // - An active connection (another tab) - responds, reject new connection as duplicate
        if (message.signal === Signals.SessionChallenge) {
          console.log('Received session challenge, responding to prove connection is alive');
          sendMessage({ signal: Signals.SessionChallengeResponse });
          return; // Don't dispatch event for internal challenge/response
        }

        // Emit signal-specific event (e.g., 'signal:start', 'signal:ice-servers')
        const signalEvent = new CustomEvent(`signal:${message.signal}`, { detail: message });
        window.dispatchEvent(signalEvent);

        // Also emit generic 'signal' event for logging/debugging
        const genericEvent = new CustomEvent('signal', { detail: message });
        window.dispatchEvent(genericEvent);
      } catch (error) {
        console.error('Failed to parse signaling message:', error);
      }
    };

    addEventListener('message', handleMessage);
    return () => removeEventListener('message', handleMessage);
  }, [addEventListener, removeEventListener, sendMessage]);

  // Send a signal through the WebSocket
  const sendSignal = useCallback((signal: SignalingMessageType) => {
    sendMessage(signal);
  }, [ sendMessage ]);

  const value = {
    isConnected,
    addSignalingListener: addEventListener,
    removeSignalingListener: removeEventListener,
    sendSignal,
    close,
    reconnect,
    reconnectionState
  };

  return (
    <SignalingContext.Provider value={value}>
      {children}
    </SignalingContext.Provider>
  );
};

// Custom hook for components to listen to specific signals
export function useSignalListener<T extends Signals>(
  signal: T,
  handler: (message: SignalingMessageType) => void
) {
  useEffect(() => {
    const eventHandler = (event: Event) => {
      const customEvent = event as CustomEvent<SignalingMessageType>;
      handler(customEvent.detail);
    };

    const eventName = `signal:${signal}`;
    window.addEventListener(eventName, eventHandler);

    return () => {
      window.removeEventListener(eventName, eventHandler);
    };
  }, [handler, signal]);
}

// Create a custom hook to use the SignalingContext
export const useSignaling = (): SignalingContextType => {
  const context = useContext(SignalingContext);
  if (context === undefined) {
    throw new Error('useSignaling must be used within a SignalingProvider');
  }
  return context;
};